import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { OidcService } from '../core/oidc/oidc.service';
import { AngularAppLogger } from '../angular-app-logger';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(
    private oidcService: OidcService,
    private router: Router,
    private logger: AngularAppLogger) {}

  canActivate(): Observable<boolean | UrlTree> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const iss = urlParams.get('iss');
    const launch = urlParams.get('launch');

    // Check for EHR flow ("ehr_" prefix or iss or launch params)
    const isEhrFlow = state?.startsWith('ehr_') || !!iss || !!launch;


    if (isEhrFlow) {
      if (localStorage.getItem('accessToken')) {
        // EHR completed, clean up and start OIDC
        window.history.replaceState({}, document.title, window.location.pathname);

        return from(this.oidcService.checkAuth()).pipe(
          map(result => {
            if (!result.isAuthenticated) {
              this.oidcService.login();
              return false;
            }
            return this.router.createUrlTree(['/landing']);
          })
        );
      } else {
        // EHR still in progress, let AppInitService handle it
        return of(true);
      }
    }

    // Check for OIDC callback (non "ehr_" prefix + state and code)
    const isOidcCallback = !isEhrFlow && !!state && !!code;
    if (isOidcCallback) {
      return from(this.oidcService.checkAuth()).pipe(
        map(result => {
          if (result.isAuthenticated) {
            return this.router.createUrlTree(['/landing']);
          }
          this.oidcService.login();
          return false;
        })
      );
    }

    // Regular OIDC check
    return from(this.oidcService.checkAuth()).pipe(
      map(result => {
        if (result.isAuthenticated) {
          return true;
        }
        this.oidcService.login();
        return false;
      }),
      catchError(error => {
        // this.logger.logError('OIDC Guard: Authentication check failed', {
        //   context: 'canActivate-error',
        //   component: 'AuthGuard',
        //   decodedValue: error
        // });
        this.oidcService.login();
        return of(false);
      })
    );
  }

  private isJwtToken(token: string): boolean {
    if (!token || typeof token !== 'string') return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    try {
      const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
      return header.alg && header.typ === 'JWT';
    } catch {
      return false;
    }
  }
}
